<template>
	<div id="app" :style="declareGlobalStyleVariables()">
		<div v-if="!pendingTaskDone">
			<pending-task
				v-bind:config="pendingTaskConfig"
				v-bind:isTaskComplete="isSiteReady"
				v-on:splashComplete="splashComplete"
			></pending-task>
		</div>
		<span v-else>
			<div id="nav">
				<div class="nav-content" v-if="isNavBarActive()">
					<language-picker />
					<!-- <span v-if="hasNavModal" v-b-modal.nav-modal id="nav-modal-link">{{ siteConfig.common.navModal.text }} |</span> -->
					<span
						v-for="(navModalLink, index) in siteConfig.common.navBar.navModalLinks"
						v-bind:key="index"
						v-b-modal="'nav-modal' + index"
					>
						<span v-if="isNavBarItemActive('navModalLinks', navModalLink)">
							<span class="nav-modal-link">{{ languageService.translate(navModalLink.text) }}</span>
							{{ " | " }}
						</span>
					</span>
					<span
						v-for="(externalLink, index) in siteConfig.common.navBar.externalLinks"
						v-bind:key="languageService.translate(externalLink.url)"
					>
						<span v-if="isNavBarItemActive('externalLinks', externalLink)">
							<b-link
								target="_blank"
								v-bind:href="languageService.translate(externalLink.url)"
								v-on:click="trackClick(index)"
								>{{ languageService.translate(externalLink.text) }}</b-link
							>
							{{ " | " }}
						</span>
					</span>
					<span
						v-for="internalLink in siteConfig.common.navBar.internalLinks"
						v-bind:key="languageService.translate(internalLink.routeName)"
					>
						<span v-if="isNavBarItemActive('internalLinks', internalLink)">
							<router-link v-bind:to="getRoute(internalLink)">
								{{ languageService.translate(internalLink.text) }}
							</router-link>
							{{ " | " }}
						</span>
					</span>
					<span v-if="siteConfig.common.navBar.navBarConcierge">
						<span v-if="isConciergeActive(conciergeLinkText)">
							<span id="conciergeLink" v-on:click="turnConciergeOn">{{ languageService.translate(conciergeLinkText) }}</span>
							<span>{{ " | " }}</span>
						</span>
					</span>
					<router-link v-bind:to="getRoute(siteConfig.common.navBar.homeLink)">{{
						languageService.translate(siteConfig.common.navBar.homeLink.text)
					}}</router-link>
					<!-- | -->
					<!-- <router-link v-bind:to="{ name: 'logout' }">log out</router-link> -->
					<span
						v-for="authenticationLink in siteConfig.common.navBar.authenticationLinks"
						v-bind:key="authenticationLink.routeName"
					>
						<span v-if="isNavBarItemActive('authenticationLinks', authenticationLink)">
							{{ " | " }}
							<router-link v-bind:to="getRoute(authenticationLink)">
								{{ languageService.translate(authenticationLink.text) }}
							</router-link>
						</span>
					</span>
				</div>
				<div class="nav-content" v-else>
					<language-picker />
				</div>
			</div>

			<!-- <b-modal v-if="hasNavModal" id="nav-modal" centered size="lg" hide-footer> -->
			<b-modal
				v-for="(navModalLink, index) in siteConfig.common.navBar.navModalLinks"
				v-bind:key="index"
				v-bind:id="'nav-modal' + index"
				centered
				v-bind:size="modalSize(navModalLink)"
				v-on:show="onModalShow(index)"
				v-on:hide="onModalHide(index)"
				hide-footer
			>
				<div>
					<iframe
						v-if="navModalLink.iFrameSrc"
						id="iframe"
						width="100%"
						height="600px"
						v-bind:src="languageService.translate(navModalLink.iFrameSrc)"
					>
					</iframe>
					<img v-else :src="languageService.translate(navModalLink.url)" />
				</div>
			</b-modal>

			<transition name="fade" mode="out-in">
				<router-view v-bind:key="$route.name" />
			</transition>
		</span>
		<div id="concierge-zendesk"></div>
	</div>
</template>

<script>
import { log } from "@/logging";
import PendingTask from "./components/PendingTask";
import LanguagePicker from "./components/LanguagePicker.vue";

const myLoggingName = "App";

export default {
	name: "App",
	props: {
		analyticsContext: Object
	},
	data: () => ({
		authenticated: false,
		siteConfig: null,
		pendingTaskDone: false,
		analyticsContexts: {}
	}),

	inject: ["authService", "configService", "zendeskManager", "languageService"],
	components: { PendingTask, LanguagePicker },
	computed: {
		conciergeLinkText() {
			return this.siteConfig.common.navBar.navBarConcierge?.text ?? "help";
		},
		hostName() {
			return window.location.hostname;
		},
		hasNavModal() {
			return this.siteConfig && this.siteConfig.common && this.siteConfig.common.navBar.navModal;
		},
		isSiteReady() {
			return this.siteConfig && true;
		},
		pendingTaskConfig() {
			// If routes are not set up, the route path is set but the route name is not, so to check if somebody is
			// reloading and going directly to a page, check that path is not "/"
			// If so, use a spinner with auto transit (instead of making them, for e.g. revisit splash when reloading lounge)
			if (this.$route.path !== "/") {
				return {
					type: "spinner",
					autoTransit: true
				};
			}
			return this.configService.getLoadingConfig();
		}
	},
	methods: {
		turnConciergeOn() {
			this.zendeskManager.open();
		},
		isAuthenticated() {
			// const isGuarded = this.siteConfig.pages.find(it => this.$route.path.startsWith(it.routePath)).isGuarded;
			// return this.authService.authed() || !isGuarded;
			return this.authService.authed();
		},
		isFilterActive(filter) {
			switch (filter) {
				case "always":
					return true;
				case "never":
					return false;
				case "authenticated":
					return this.isAuthenticated();
				case "unauthenticated":
					return !this.isAuthenticated();
				case "anonymousMode":
					return this.isAnonymousMode();
				case "authMode":
					return !this.isAnonymousMode();
				default:
					log(myLoggingName, "isNavBarActive", "unsupportedfilter", filter);
					return false;
			}
		},
		isNavBarActive() {
			const activeMode = this.configService.determineActiveSiteMode(this.siteConfig);
			const filter = this.configService.getActiveSiteMode(this.siteConfig, activeMode.mode).navBar?.globalActiveFilter;
			if (filter) {
				return this.isFilterActive(filter);
			} else {
				return this.isFilterActive("authenticated");
			}
		},
		isConciergeActive(text) {
			return this.isNavBarItemActive("navBarConcierge", { text: text });
		},
		isNavBarItemActive(navBarItemType, navBarItem) {
			// get the mode's navbar filter configurations
			const activeMode = this.configService.determineActiveSiteMode(this.siteConfig);
			const navBar = this.configService.getActiveSiteMode(this.siteConfig, activeMode.mode).navBar;
			let filters = new Array();
			if (navBar && navBar.filters && navBar.filters[navBarItemType]) {
				filters = navBar.filters[navBarItemType];
			}
			// find those that match the supplied navar item (note that the merge method checks the routename property of the passed in items but the configuration maps filters based on the text so the
			// item text is mapped to the routeName field - slightly kludgy )
			const matches = this.configService.merge(filters, [{ routeName: navBarItem.text }]);

			// also note that the merge can be set to explicit, in which case it returns a list of items that may or may not match the navbar item, so check whether the returned items have a match
			const nonNullMatches = matches.filter(it => it.items.some(filterItem => filterItem === navBarItem.text));

			// if there are any, check whether the first one's value "isActive", else test against "authenticated"
			if (nonNullMatches.length > 0 && nonNullMatches[0].value != null) {
				const rc = this.isFilterActive(nonNullMatches[0].value);
				return rc;
			} else {
				const rc = this.isFilterActive("authenticated");
				return rc;
			}
		},
		// Event handler for pending task component indicating that user has clicked the "Proceed to site" button
		splashComplete() {
			this.pendingTaskDone = true;
		},
		isAnonymousMode() {
			return this.authService.isAnonymousMode();
		},
		modalSize(navModalLink) {
			// eslint-disable-next-line prettier/prettier
			if (navModalLink?.size) {
				return navModalLink.size;
			} else {
				return "lg";
			}
		},
		// This was added to support adding parameters to internal routes as part of an early version of support for adding a concierge/help link to the navbar. A different solution was found for that link, but this may be useful for other links.
		// Leaving in for now.
		getRoute(item) {
			if (item.routeName.params) {
				// Obsolete - confirm this is not used and remove in next version
				return { name: item.routeName.name, params: item.routeName.params };
			}
			return { name: this.languageService.translate(item.routeName) };
		},
		declareGlobalStyleVariables() {
			if (!this.siteConfig) return {};
			const styles = {};
			Object.keys(this.siteConfig.common.globalStyleVariables).forEach(key => {
				styles["--" + key] = this.siteConfig.common.globalStyleVariables[key];
			});
			return styles;
		},
		onModalShow(itemConfig) {
			let id = "nav-modal" + itemConfig.toString();

			log(myLoggingName, "mounted Modal is about to be opened", { itemId: id });
			const context = this.analyticsContexts[id];
			context.ingress(new Map());
		},
		onModalHide(itemConfig) {
			let id = "nav-modal" + itemConfig.toString();
			log(myLoggingName, "mounted Modal is about to be closed", { itemId: id });
			const context = this.analyticsContexts[id];
			context.egress("closed");
		},

		addChildrenToAnalytics() {
			const capturedVue = this;
			this.siteConfig.common.navBar.navModalLinks.forEach((item, index) => {
				let name = item.text.toString();
				let id = "nav-modal" + index.toString();

				capturedVue.analyticsContexts[id] = capturedVue.analyticsContext.addChild(name);
			});
			this.siteConfig.common.navBar.externalLinks.forEach((item, index) => {
				let name = item.text.toString();
				let id = "externallink" + index.toString();

				capturedVue.analyticsContexts[id] = capturedVue.analyticsContext.addChild(name);
			});
		},
		trackClick(index) {
			//			log(myLoggingName,index);
			let id = "externallink" + index.toString();
			const roomAnalyitcsContext = this.analyticsContexts[id];
			const values = new Map();
			const event = roomAnalyitcsContext.makeEvent("clicked", "event", values);
			roomAnalyitcsContext.recordEvent(event);
		}
	},
	created() {
		this.created = true;
	},
	mounted() {
		this.mountedCount++;
		let capturedVue = this;
		this.configService.getSiteConfig().then(config => {
			// setTimeout(() => {
			// 	console.log("Start delay");
			// 	capturedVue.siteConfig = config;
			// 	console.log("End delay");
			// }, 5000);
			capturedVue.siteConfig = config;
			capturedVue.addChildrenToAnalytics();
			capturedVue.authenticated = this.isAuthenticated();
		});
	},
	beforeUpdate() {
		this.authenticated = this.isAuthenticated();
	}
};
</script>

<style lang="scss">
@import "~normalize.css/normalize.css";
@import "scss/games-modal";
@import "scss/nav-modal";
@import "scss/common";

* {
	box-sizing: border-box;
}

html,
body {
	height: 100%;
	margin: 0;
}

body {
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 250ms linear;
}

.fade-enter,
.fade-leave-to {
	opacity: 0.5;
}

.logout-link {
	cursor: pointer;

	&:hover {
		text-decoration: underline !important;
	}
}

// #nav-modal-link {
// 	cursor: pointer;
// 	float: left;
// 	padding-right: 3px;

// 	&:hover {
// 		text-decoration: underline !important;
// 	}
// }

nav-modal-link {
	cursor: pointer;
	float: left;
	padding-right: 3px;

	&:hover {
		text-decoration: underline !important;
	}
}

#conciergeLink {
	cursor: pointer;
}

#app {
	font-family: Avenir, Helvetica, Arial, sans-serif;
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
	text-align: center;

	color: var(--primary-text-colour);

	height: 100%;
}

body,
html {
	margin: 0;
	padding: 0;
}

body {
	object-position: 50% 50%;
	background-size: cover;
	background-position: center center;
	background-repeat: no-repeat;
	height: 100vh;
}

#nav {
	width: 100%;
	margin: 0 auto;

	z-index: 10;
	text-align: right;
	font-weight: bold;
	position: relative;
	color: var(--nav-text-colour);

	.nav-content {
		position: absolute;
		padding: 19px;
		right: 0;
		background-color: #ffffffbd;
	}

	a {
		color: var(--nav-text-colour);

		&.router-link-exact-active {
			color: var(--active-route-link-colour);
		}
	}
}

img {
	max-width: 100%;
}

@media (max-width: $screen-mobile-max) {
	.overlay {
		display: none;
	}
}

.home-bg {
	position: relative;
	transform-origin: top;
}

.bg-image {
	max-width: none;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
}

.home-page {
	display: grid;
	justify-content: center;
	align-items: center;

	width: 100%;
	height: 100%;
	box-sizing: content-box;

	background: linear-gradient(to top right, var(--primary-colour), var(--primary-gradient-colour));
}
</style>
